import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, OTP, Toaster } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { MdEast } from 'react-icons/md';
import toast from 'react-hot-toast';
import type { ApiResponse } from '../../utils';
import { useResendEmailVerification } from '../../hooks/useAuthQuery';
import { useResendCooldown } from '../../hooks/useResendCooldown';

interface EmailVerificationFormData {
  otp: string;
}

const EmailVerificationForm = () => {
  const [userEmail] = useState(localStorage.getItem('user-email') || '');
  const [isLoading, setIsLoading] = useState(false);

  // Apr-19 fix (Issue 1 Apr-18): countdown is
  // frontend-driven, no longer depends on backend `expires_at` which is
  // masked to null post-Apr-18. Matches the backend's 5
  // req/60s per-IP throttle so UI never invites a 429-able call.
  const { secondsLeft, isOnCooldown, startCooldown } = useResendCooldown(60);

  const { emailVerification } = useAuth();
  const { mutateAsync: resendEmailVerificationMutation } =
    useResendEmailVerification();
  const {
    control,
    watch,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailVerificationFormData>({
    defaultValues: {
      otp: '',
    },
  });

  // Start cooldown on mount — a verification code was just sent during
  // signup or signin-unverified redirect. User won't see "Resend" link
  // for the first 60s; matches prior UX shape and backend throttle.
  useEffect(() => {
    if (userEmail) startCooldown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabledSubmit = watch('otp').length !== 4;

  const onSubmit = async (data: EmailVerificationFormData) => {
    setIsLoading(true);
    try {
      await emailVerification({
        email: userEmail,
        code: data.otp,
      });
    } catch (error) {
      setError('otp', { message: (error as ApiResponse)?.message as string });
      console.error('Email verification failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resendEmailVerificationCode = async () => {
    if (isOnCooldown) return;

    await resendEmailVerificationMutation(
      { email: userEmail },
      {
        onSuccess: async () => {
          toast.custom(t => (
            <Toaster
              message='Verification code has been resent'
              type='success'
              id={t.id}
            />
          ));
          startCooldown();
        },
        onError: (error: any) => {
          toast.custom(t => (
            <Toaster
              message={error?.message as string}
              type='error'
              id={t.id}
            />
          ));
          return error;
        },
      }
    );
  };

  return (
    <form className='w-full' onSubmit={handleSubmit(onSubmit)}>
      <div className='mb-6'>
        <Controller
          control={control}
          name='otp'
          rules={{
            required: { value: true, message: 'Please enter the OTP code' },
          }}
          render={({ field }) => (
            <OTP
              inputWrapperclassName='!justify-between'
              length={4}
              value={field.value}
              onChange={field.onChange}
              size='xl'
              separator={true}
              autoFocus={true}
              color={errors.otp?.message ? 'error' : 'default'}
              helperText={errors.otp?.message as string}
              helperTextClassName='text-red-500'
            />
          )}
        />
      </div>

      <Button
        type='submit'
        disabled={disabledSubmit}
        isLoading={isLoading}
        className='group relative w-full'
      >
        Verify <MdEast size={24} />
      </Button>

      <p className='font-medium flex items-center justify-between gap-2 mt-4'>
        {isOnCooldown ? (
          <span></span>
        ) : (
          <span>
            Didn't receive?{' '}
            <button
              type='button'
              disabled={isOnCooldown}
              className='text-link cursor-pointer hover:text-gray-600 disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50'
              onClick={resendEmailVerificationCode}
            >
              Resend
            </button>
          </span>
        )}

        {isOnCooldown && (
          <span className='text-body-400 text-sm'>
            Resend in <span className='font-semibold text-heading-500'>{secondsLeft}s</span>
          </span>
        )}
      </p>
    </form>
  );
};

export default EmailVerificationForm;
